import api from '@/shared/utils/apiConfig';
import { getApiErrorMessageByCode } from '@/shared/utils/apiError';
import i18n from '@/shared/i18n';
import type {
  OvertimeRequest,
  OvertimeRequestStatus,
  OvertimeTypeRecord,
  OvertimeTypeStatus,
} from '../types/overtime';

interface ApiListResponse<T> {
  success: boolean;
  data: T;
  count?: number;
  total?: number;
  page?: number;
  pages?: number;
}

interface ApiItemResponse<T> {
  success: boolean;
  data: T;
}

export interface ListOvertimeTypesParams {
  status?: OvertimeTypeStatus;
}

export interface CreateOvertimeTypePayload {
  code: string;
  name: string;
  color?: string;
  multiplier: number;
  sortOrder?: number;
}

export interface UpdateOvertimeTypePayload {
  code?: string;
  name?: string;
  color?: string;
  multiplier?: number;
  sortOrder?: number;
}

export interface ListOvertimeRequestsParams {
  staffId?: string;
  clinicId?: string;
  status?: OvertimeRequestStatus;
  from?: string;
  to?: string;
  search?: string;
  sort?: string;
  page?: number;
  limit?: number;
}

export interface CreateOvertimeRequestPayload {
  staffId?: string;
  clinicId: string;
  date: string;
  startTime: string;
  endTime: string;
  typeId: string;
  reason: string;
}

export const listOvertimeTypes = async (
  params: ListOvertimeTypesParams = {}
): Promise<OvertimeTypeRecord[]> => {
  const response = await api.get<ApiListResponse<OvertimeTypeRecord[]>>('/overtime/types', {
    params,
  });
  return response.data.data ?? [];
};

export const createOvertimeType = async (
  payload: CreateOvertimeTypePayload
): Promise<OvertimeTypeRecord> => {
  const response = await api.post<ApiItemResponse<OvertimeTypeRecord>>('/overtime/types', payload);
  return response.data.data;
};

export const updateOvertimeType = async (
  id: string,
  payload: UpdateOvertimeTypePayload
): Promise<OvertimeTypeRecord> => {
  const response = await api.put<ApiItemResponse<OvertimeTypeRecord>>(
    `/overtime/types/${id}`,
    payload
  );
  return response.data.data;
};

export const updateOvertimeTypeStatus = async (
  id: string,
  status: OvertimeTypeStatus
): Promise<OvertimeTypeRecord> => {
  const response = await api.patch<ApiItemResponse<OvertimeTypeRecord>>(
    `/overtime/types/${id}/status`,
    { status }
  );
  return response.data.data;
};

export const listOvertimeRequests = async (
  params: ListOvertimeRequestsParams = {}
): Promise<{ data: OvertimeRequest[]; total: number }> => {
  const response = await api.get<ApiListResponse<OvertimeRequest[]>>('/overtime/requests', {
    params,
  });
  return {
    data: response.data.data ?? [],
    total: response.data.total ?? response.data.data?.length ?? 0,
  };
};

export const createOvertimeRequest = async (
  payload: CreateOvertimeRequestPayload
): Promise<OvertimeRequest> => {
  const response = await api.post<ApiItemResponse<OvertimeRequest>>(
    '/overtime/requests',
    payload
  );
  return response.data.data;
};

export const approveOvertimeRequest = async (id: string): Promise<OvertimeRequest> => {
  const response = await api.post<ApiItemResponse<OvertimeRequest>>(
    `/overtime/requests/${id}/approve`
  );
  return response.data.data;
};

export const rejectOvertimeRequest = async (
  id: string,
  rejectionReason: string
): Promise<OvertimeRequest> => {
  const response = await api.post<ApiItemResponse<OvertimeRequest>>(
    `/overtime/requests/${id}/reject`,
    { rejectionReason }
  );
  return response.data.data;
};

export const cancelOvertimeRequest = async (id: string): Promise<OvertimeRequest> => {
  const response = await api.post<ApiItemResponse<OvertimeRequest>>(
    `/overtime/requests/${id}/cancel`
  );
  return response.data.data;
};

export const getOvertimeErrorMessage = (err: unknown, fallback: string): string =>
  getApiErrorMessageByCode(err, fallback, {
    ATTENDANCE_MONTH_LOCKED: i18n.t('overtime:errors.ATTENDANCE_MONTH_LOCKED'),
    OVERTIME_APPROVER_REQUIRED: i18n.t('overtime:errors.OVERTIME_APPROVER_REQUIRED'),
    NOT_ATTENDANCE_APPROVER: i18n.t('overtime:errors.NOT_ATTENDANCE_APPROVER'),
  });
